import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { navigateToDashboard, getEffectiveLangId } from "../utils/navigationHelpers";
import { authFetch } from "../utils/auth";
import LoadingScreen from "../components/common/LoadingScreen";
import StoryReader from "../components/storytelling/StoryReader";
import StoryQuiz from "../components/storytelling/StoryQuiz";
import InGameSettingsModal, { SettingsIcon } from "../components/common/InGameSettingsModal";
import BackToDashboardButton from "../components/common/BackToDashboardButton";
import SessionCompleteCard from "../components/common/SessionCompleteCard";
import "../assets/css/storytelling.css";

function StoryPage() {
    const urlParams = new URLSearchParams(window.location.search);
    const storyId = urlParams.get("story_id");
    const langId = getEffectiveLangId(urlParams.get("lang_id"));

    const [story, setStory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [gameState, setGameState] = useState("reading"); // reading, quiz, completed
    const [showSettings, setShowSettings] = useState(false);
    const [enablePronunciation, setEnablePronunciation] = useState(true);

    useEffect(() => {
        if (!storyId) {
            setError(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        fetch(`${API_BASE_URL}/api/story?story_id=${storyId}`)
            .then(res => res.json())
            .then(data => {
                if (data.error) {
                    setError(true);
                    console.error(data.error);
                } else {
                    setStory(data);
                }
            })
            .catch(err => {
                console.error(err);
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [storyId]);

    const handleBackToDashboard = () => {
        navigateToDashboard("storytelling");
    };

    const handleReadingComplete = () => {
        setGameState("quiz");
    };

    const handleQuizComplete = (score, total) => {
        // Post score to familiarity backend
        const familiarity_score = total > 0 ? score / total : 1.0;

        authFetch(`${API_BASE_URL}/api/upsert-familiarity`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                tile_id: parseInt(story.tile_id, 10),
                familiarity_score: familiarity_score,
                completed: true
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.error) {
                    console.error("Error upserting familiarity:", data.error);
                } else {
                    console.log("Successfully recorded story familiarity progress.");
                }
            })
            .catch(err => console.error("Network error upserting familiarity:", err))
            .finally(() => {
                setGameState("completed");
            });
    };

    if (loading) {
        return (
            <LoadingScreen
                title="Loading story content..."
                onBack={handleBackToDashboard}
                backLabel="Dashboard"
            />
        );
    }

    if (error || !story) {
        return (
            <div className="story-page-container">
                <div className="book-container" style={{ textAlign: "center", padding: "clamp(24px, 5vw, 80px)" }}>
                    <p>Error loading this story.</p>
                    <BackToDashboardButton centered onClick={handleBackToDashboard} />
                </div>
            </div>
        );
    }

    if (gameState === "completed") {
        return (
            <SessionCompleteCard
                title="Story Completed"
                stats={story?.title ? [{ label: "Story", value: story.title }] : []}
                onBack={handleBackToDashboard}
            />
        );
    }

    return (
        <div className="story-page-container">
            <header className="story-header">
                <BackToDashboardButton onClick={handleBackToDashboard} />
                <div className="story-header__title-area">
                    <h1 className="story-header__title">{story.title}</h1>
                </div>
                <div className="story-header__right">
                    <button
                        type="button"
                        aria-label="Settings"
                        className="in-game-settings-btn"
                        style={{ position: 'static' }}
                        onClick={() => setShowSettings(true)}
                    >
                        <SettingsIcon size={24} />
                    </button>
                </div>
            </header>

            <InGameSettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                title="Settings"
            >
                <div className="in-game-settings-section">
                    <label className="in-game-settings-checkbox-label">
                        <input
                            type="checkbox"
                            checked={enablePronunciation}
                            onChange={e => setEnablePronunciation(e.target.checked)}
                            className="in-game-settings-checkbox"
                        />
                        Enable Pronunciation Questions
                    </label>
                    <div className="in-game-settings-desc">
                        Practice your speaking skills with interactive audio questions.
                    </div>
                </div>
            </InGameSettingsModal>

            <main className="book-container">
                {gameState === "reading" && (
                    <StoryReader
                        story={story}
                        langId={langId}
                        enablePronunciation={enablePronunciation}
                        onComplete={handleReadingComplete}
                    />
                )}
                {gameState === "quiz" && (
                    <StoryQuiz
                        story={story}
                        enablePronunciation={enablePronunciation}
                        onComplete={handleQuizComplete}
                        langId={langId}
                    />
                )}
            </main>
        </div>
    );
}

export default StoryPage;
